import type { ReactNode } from "react";
import { useId } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Mail, Phone } from "lucide-react";
import { Logo } from "@/components/ui/Logo";
import { SITE, HAS_ADDRESS, HAS_PHONE, formatAddressInline } from "@/placeholder/site.config";

/**
 * Canonical RideReserv contact channels. Sourced from the static
 * `site.config` module — still compile-time constants, never derived from user
 * input, query params, or state (Corridor: tel:/mailto: hrefs must be hardcoded
 * to avoid protocol injection). The footer is the "Contact" nav target, so these
 * surface here now that the standalone /contact page is gone; the same values
 * appear in the legal pages.
 */
const SUPPORT_EMAIL = SITE.supportEmail;
const SUPPORT_PHONE_DISPLAY = SITE.supportPhoneDisplay;
const SUPPORT_PHONE_TEL = SITE.supportPhoneE164;

/** A single footer link, with an optional disambiguating accessible name. */
interface FooterLinkSpec {
  /** The destination route (internal SPA path) or in-page anchor. */
  to: string;
  /** The visible link label. */
  label: string;
  /**
   * Accessible name override, used where the visible label is ambiguous out of
   * context — e.g. two "Get started" links pointing at different signup flows.
   * Satisfies WCAG 2.4.4 (Link Purpose) for screen-reader users browsing the
   * links list.
   */
  ariaLabel?: string;
}

/** A single navigation column in the footer's link grid. */
interface FooterColumn {
  /** The column caption (rendered as a non-heading label, not an `<h*>`). */
  heading: string;
  /** The links rendered beneath the caption, in order. */
  links: readonly FooterLinkSpec[];
}

/**
 * Footer link columns. Organized by audience and intent rather than mirroring
 * the header nav — the footer is a sitemap + dual-audience funnel, deliberately
 * differentiated from the thin top nav. The Product/Company links point at the
 * homepage in-page anchors (the standalone marketing pages were retired in
 * favor of homepage sections).
 */
const FOOTER_COLUMNS: readonly FooterColumn[] = [
  {
    heading: "Product",
    links: [
      { to: "/#features", label: "Features" },
      { to: "/#how-it-works", label: "How it works" },
      { to: "/#pricing", label: "Pricing" },
    ],
  },
  {
    heading: "For operators",
    links: [
      {
        to: "/register?type=business",
        label: "Get started",
        ariaLabel: "Get started as an operator",
      },
      { to: "/#features", label: "Features" },
    ],
  },
  {
    heading: "For drivers",
    links: [
      {
        to: "/register?type=driver",
        label: "Get started",
        ariaLabel: "Get started as a driver",
      },
      { to: "/#features", label: "Features" },
    ],
  },
  {
    heading: "Company",
    links: [
      { to: "/#about", label: "About" },
      { to: "/#contact", label: "Contact" },
    ],
  },
] as const;

/** Legal links rendered in the baseline bar. */
const LEGAL_LINKS: readonly { to: string; label: string }[] = [
  { to: "/terms", label: "Terms of Service" },
  { to: "/privacy", label: "Privacy Policy" },
  { to: "/driver-terms", label: "Driver Terms" },
  { to: "/sms", label: "SMS Terms" },
] as const;

/**
 * Rich, multi-column site footer for every public/marketing page. Doubles as
 * the "Contact" surface (id="contact") now that the standalone contact page is
 * gone — it carries the support email, phone, and the business-inquiry CTA.
 *
 * Intentionally always-dark (uses the `bg-ink` / `text-ink-foreground` token
 * pair, which is constant across light and dark themes — flip-safe). It is a
 * full sitemap + dual-audience funnel rather than an echo of the top nav: a
 * brand column with tagline, a contact block, four navigation columns (Product,
 * For companies, For drivers, Company), and a baseline bar carrying legal links
 * plus a sign-in affordance. Navigation is internal SPA routing via
 * react-router-dom `Link`; the mailto:/tel: hrefs are hardcoded static
 * literals. Takes no props.
 * @returns The site footer.
 */
export function SiteFooter() {
  const year = new Date().getFullYear();
  // Stable prefix for per-column caption ids (groups labelled via aria-labelledby).
  const captionPrefix = useId();

  return (
    // id="contact" makes the footer the "Contact" nav target (header/footer
    // links + the final-CTA "Talk to us" jump). scroll-mt clears the sticky
    // header so the contact block isn't tucked under it on deep-link/scroll.
    <footer
      id="contact"
      className="scroll-mt-16 bg-ink text-ink-foreground"
    >
      <div className="mx-auto w-full max-w-7xl px-4 py-12 sm:px-6 lg:px-8 lg:py-16">
        <div className="flex flex-col gap-10 lg:flex-row lg:gap-16">
          {/* Brand + contact column */}
          <div className="flex max-w-xs flex-col gap-4 lg:flex-1">
            <Logo asLink tone="inverse" />
            <p className="text-pretty text-sm leading-relaxed text-ink-foreground/70">
              Reservations &amp; dispatch for professional ground transportation.
            </p>

            {/* Contact channels — hardcoded mailto:/tel: literals. */}
            <ul className="flex flex-col gap-2 not-italic">
              <li>
                <a
                  href={`mailto:${SUPPORT_EMAIL}`}
                  className="inline-flex min-h-11 items-center gap-2 text-sm text-ink-foreground/70 transition-colors pointer-fine:min-h-9 hover:text-ink-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400"
                >
                  <Mail
                    className="h-3.5 w-3.5 shrink-0 text-ink-foreground/50"
                    strokeWidth={1.75}
                    aria-hidden="true"
                  />
                  {SUPPORT_EMAIL}
                </a>
              </li>
              {HAS_PHONE && (
                <li>
                  <a
                    href={`tel:${SUPPORT_PHONE_TEL}`}
                    className="inline-flex min-h-11 items-center gap-2 font-mono text-sm tabular-nums text-ink-foreground/70 transition-colors pointer-fine:min-h-9 hover:text-ink-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400"
                  >
                    <Phone
                      className="h-3.5 w-3.5 shrink-0 text-ink-foreground/50"
                      strokeWidth={1.75}
                      aria-hidden="true"
                    />
                    {SUPPORT_PHONE_DISPLAY}
                  </a>
                </li>
              )}
            </ul>

            <Link
              to="/register?type=business"
              className="group inline-flex min-h-11 items-center gap-1.5 self-start rounded-sm text-sm font-medium text-primary-400 transition-colors pointer-fine:min-h-9 hover:text-ink-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400"
            >
              Start a business inquiry
              <ArrowUpRight
                className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                strokeWidth={1.75}
                aria-hidden="true"
              />
            </Link>

            {/* Operating entity + mailing address (establishes business identity
                for the public site; the address line omits cleanly until the
                value is configured in site.config). */}
            <address className="text-xs not-italic leading-relaxed text-ink-foreground/50">
              {SITE.dba} is a service of {SITE.legalEntity}.
              {HAS_ADDRESS && (
                <>
                  <br />
                  {formatAddressInline()}
                </>
              )}
            </address>
          </div>

          {/* Navigation columns. Captions are non-heading labels (rendered as
              <p>, not <h*>) so the footer doesn't inject four entries into every
              page's heading outline; each column is a labelled group. */}
          <nav
            aria-label="Footer"
            className="grid flex-1 grid-cols-2 gap-x-8 gap-y-8 sm:grid-cols-4"
          >
            {FOOTER_COLUMNS.map((column, columnIndex) => {
              const captionId = `${captionPrefix}-col-${String(columnIndex)}`;
              return (
                <div
                  key={column.heading}
                  role="group"
                  aria-labelledby={captionId}
                  className="flex flex-col"
                >
                  <p
                    id={captionId}
                    className="text-xs font-semibold uppercase tracking-wider text-ink-foreground/50"
                  >
                    {column.heading}
                  </p>
                  <ul className="mt-3 flex flex-col gap-y-0.5">
                    {column.links.map((link) => (
                      <li key={`${column.heading}-${link.label}-${link.to}`}>
                        <FooterLink to={link.to} ariaLabel={link.ariaLabel}>
                          {link.label}
                        </FooterLink>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </nav>
        </div>

        {/* Baseline bar: hairline divider, copyright, legal + account links */}
        <div className="mt-12 flex flex-col gap-4 border-t border-ink-foreground/10 pt-6 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-ink-foreground/60">
            <span className="font-mono tabular-nums">&copy; {year}</span>{" "}
            RideReserv. All rights reserved.
          </p>
          <nav
            aria-label="Legal and account"
            className="flex flex-wrap items-center gap-x-5 gap-y-1"
          >
            {LEGAL_LINKS.map((link) => (
              <FooterLink key={link.to} to={link.to} muted>
                {link.label}
              </FooterLink>
            ))}
            <FooterLink to="/login" muted>
              Sign in
            </FooterLink>
          </nav>
        </div>
      </div>
    </footer>
  );
}

interface FooterLinkProps {
  /** The destination route (internal SPA path) or in-page anchor. */
  to: string;
  /** The visible link label. */
  children: ReactNode;
  /**
   * Accessible name override for ambiguous labels (e.g. duplicate "Get started"
   * links to different flows). When set, it becomes the link's accessible name
   * while the visible text stays unchanged.
   */
  ariaLabel?: string;
  /** Render smaller, lower-contrast styling for the baseline bar. */
  muted?: boolean;
}

/**
 * A footer link styled for the always-dark ink surface: muted by default,
 * brightening to full ink-foreground on hover, with a 44px coarse tap target
 * that densifies on fine pointers and a visible focus ring.
 * @param root0 - Component props.
 * @param root0.to - The destination route.
 * @param root0.children - The visible link label.
 * @param root0.ariaLabel - Accessible name override for ambiguous labels.
 * @param root0.muted - Render baseline-bar styling.
 * @returns The footer link.
 */
function FooterLink({ to, children, ariaLabel, muted = false }: FooterLinkProps) {
  return (
    <Link
      to={to}
      aria-label={ariaLabel}
      className={
        muted
          ? "inline-flex min-h-11 items-center text-sm text-ink-foreground/60 transition-colors pointer-fine:min-h-8 hover:text-ink-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400"
          : "inline-flex min-h-11 items-center text-sm text-ink-foreground/70 transition-colors pointer-fine:min-h-9 hover:text-ink-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400"
      }
    >
      {children}
    </Link>
  );
}
